import { Injectable } from '@angular/core';

import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { WithListFileResponse } from 'src/app/components/scrolling-list';
import { StorageFile } from 'src/app/storage/services/storage-utils.service';
import { environment } from 'src/environments/environment';
import { StorageApiSearch } from './api-utils.service';
import { ApiService } from './api.service';

export interface TransferResponse {
  conversionTask: {
    status: string;
    assetPath: string;
  };
}

export const NOT_ALLOWED_FILE_NAMES = [
  '/',
  '\\',
  '..',
  '..\\',
  '.',
  '..\\.',
  '--TRASH--',
  '--TRASH--/',
];

@Injectable({
  providedIn: 'root',
})
export class StorageApiService extends ApiService<any> {
  protected APIUrl =
    environment.SPRING_STORAGE_ENDPOINT_URL + this.getResourceUrl();

  constructor(
    protected httpClient: HttpClient,
    protected companyStateService: CompanyStateService,
  ) {
    super(httpClient);
  }

  getResourceUrl(): string {
    return '/v3';
  }

  _mapFileNames(files: StorageFile[]) {
    let fileNames: string[] = [];
    if (files?.length && files[0].name) {
      fileNames = files.map((file) => {
        return file.name;
      });
    }
    return fileNames;
  }

  list(search: StorageApiSearch): Promise<WithListFileResponse> {
    let params = new HttpParams().set(
      'companyId',
      search.companyId || this.companyStateService.getSelectedCompanyId(),
    );

    if (search.folderPath) {
      params = params.append('folder', search.folderPath);
    }
    if (search.file) {
      params = params.append('file', search.file);
    }
    if (search.assetId) {
      params = params.append('assetId', search.assetId);
    }
    if (search.assetType) {
      params = params.append('assetType', search.assetType);
    }

    return firstValueFrom(
      super.requestAsObservable('', 'GET', 'files', params),
    );
  }

  deleteFiles(selectedFiles: { items: StorageFile[] }): Promise<any> {
    const params = new HttpParams().set(
      'companyId',
      this.companyStateService.getSelectedCompanyId(),
    );

    const body = {
      files: this._mapFileNames(selectedFiles.items),
    };

    return firstValueFrom(
      super.requestAsObservable('', 'DELETE', 'files', params, body),
    );
  }

  moveToTrash(selectedFiles: { items: StorageFile[] }): Promise<any> {
    const params = new HttpParams().set(
      'companyId',
      this.companyStateService.getSelectedCompanyId(),
    );

    const body = {
      files: this._mapFileNames(selectedFiles.items),
    };

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'trash/move', params, body),
    );
  }

  restoreFromTrash(selectedFiles: { items: StorageFile[] }): Promise<any> {
    const params = new HttpParams().set(
      'companyId',
      this.companyStateService.getSelectedCompanyId(),
    );

    const body = {
      files: this._mapFileNames(selectedFiles.items),
    };

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'trash/restore', params, body),
    );
  }

  createFolder(folderPathName: string): Promise<any> {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('folder', folderPathName);

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'folder', params),
    );
  }

  getFolderContents(folderPathName: string): Promise<any> {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('folder', folderPathName);

    return firstValueFrom(
      super.requestAsObservable('', 'GET', 'folder', params),
    );
  }

  getResumableUploadURI(fileName: string, fileType: string): Promise<any> {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('fileName', encodeURIComponent(fileName))
      .set('fileType', fileType)
      .set('origin', window.location.origin);

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'files/resumableUploadURI', params),
    );
  }

  getSignedDownloadURI(file: StorageFile): Promise<any> {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('fileName', encodeURIComponent(file.name));

    return firstValueFrom(
      super.requestAsObservable('', 'GET', 'files/signedDownloadURI', params),
    );
  }

  rename(originalName: string, newName: string): Promise<any> {
    if (originalName === newName) {
      return Promise.reject({
        status: 400,
        message: 'must-be-different',
      });
    } else {
      const params = new HttpParams()
        .set('companyId', this.companyStateService.getSelectedCompanyId())
        .set('sourceName', originalName)
        .set('destinationName', newName);

      return firstValueFrom(
        super.requestAsObservable('', 'PATCH', 'files/rename', params),
      );
    }
  }

  duplicate(sourceName: string): Promise<any> {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('sourceName', sourceName);

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'files/duplicate', params),
    );
  }

  getEncodingUploadURI(fileName: string): Promise<any> {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('fileName', encodeURIComponent(fileName));

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'encoding/uploadURI', params),
    );
  }

  startEncodingTask(data: {
    taskToken: string;
    fileUUID: string;
    fileName: string;
  }): Promise<any> {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('taskToken', data.taskToken)
      .set('fileUUID', data.fileUUID)
      .set('fileName', data.fileName);

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'encoding/startTask', params),
    );
  }

  acceptEncodedFile(fileName: string): Promise<any> {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('fileName', encodeURIComponent(fileName));

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'encoding/acceptFile', params),
    );
  }

  requestServiceAssets(
    companyId: string,
    fileName: string,
    assetType: string,
  ): Promise<any> {
    const params = new HttpParams()
      .set(
        'companyId',
        companyId || this.companyStateService.getSelectedCompanyId(),
      )
      .set('file', fileName)
      .set('assetType', assetType);

    return firstValueFrom(
      super.requestAsObservable(
        '',
        'POST',
        'files/requestServiceAssets',
        params,
      ),
    );
  }

  refreshFileMetadata(fileName: string, remainingAttempts = 2): Promise<any> {
    console.log(
      'Attempt #' + remainingAttempts + ' to get metadata for: ' + fileName,
    );

    return this.list({
      file: fileName,
    }).then((resp) => {
      var file = resp && resp.files && resp.files[0];

      if (
        file &&
        (!file.metadata || file.metadata['needs-thumbnail-update'] !== 'true')
      ) {
        return file;
      } else if (file && remainingAttempts > 0) {
        return this.refreshFileMetadata(fileName, remainingAttempts - 1);
      } else {
        console.log('Error refreshing metadata', file?.name);

        return file;
      }
    });
  }

  transfer(
    sourceFilesUrls: string[],
    destinationPath: string,
  ): Promise<TransferResponse> {
    const body = {
      companyId: this.companyStateService.getSelectedCompanyId(),
      sourceFilesUrls: sourceFilesUrls.map((url) => encodeURIComponent(url)),
      destinationPath,
    };

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'transfer/user', undefined, body),
    );
  }

  getAsset(assetId: string, assetType: string): Promise<any> {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('assetId', assetId)
      .set('assetType', assetType);

    return firstValueFrom(
      super.requestAsObservable('', 'GET', 'files', params),
    );
  }
}
